import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, Edit, Trash2, Package } from "lucide-react";
import { salesAPI, getApiErrorPayload } from "@/lib/api";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";
import { Loading } from "@/components/ui/loader";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { SplitBillSummaryCard, type SplitBillShare } from "@/features/sales/components/SplitBillSummaryCard";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface SalesItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage: number;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
}

interface SalesTransaction {
	id: string;
	invoiceNumber: string;
	customerId: string;
	customer?: {
		id: string;
		firstName: string;
		lastName: string;
		email: string;
		phone: string;
		address: string;
	};
	items: SalesItem[];
	subtotal: number;
	discountAmount: number;
	taxAmount: number;
	shippingAmount: number;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	paymentStatus: "pending" | "partial" | "paid";
	paymentMethod?: "cash" | "card" | "bank_transfer" | "check" | "credit";
	status: "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";
	notes: string;
	saleDate: string;
	dueDate: string;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
	splitStatus?: string | null;
	splitChildren?: SplitBillShare[];
}

const InvoiceDetailPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const currency = useCurrency();
	const [loading, setLoading] = useState(false);
	const [salesTransaction, setSalesTransaction] = useState<SalesTransaction | null>(null);
	const [pdfLoading, setPdfLoading] = useState(false);

	useEffect(() => {
		if (id) {
			fetchInvoice();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	const fetchInvoice = async () => {
		try {
			setLoading(true);
			const data = await salesAPI.getById(id!);
			setSalesTransaction(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("invoices.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/invoices");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async () => {
		if (await confirmAction(t("invoices.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await salesAPI.delete(id!);
				toast({
					title: t("common.success"),
					description: t("invoices.messages.deleteSuccessDescription"),
				});
				navigate("/invoices");
			} catch (error) {
				const payload = getApiErrorPayload(error);
				const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
				toast({
					title: t("common.error"),
					description: errorMessage || t("common.errors.unexpected"),
					variant: "destructive",
				});
			}
		}
	};

	const generatePDF = async () => {
		if (!id) return;
		try {
			setPdfLoading(true);
			const blob = await salesAPI.downloadPdf(id);
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `invoice-${salesTransaction?.invoiceNumber ?? id}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
			toast({
				title: t("common.success"),
				description: t("invoices.messages.pdfGenerated"),
			});
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description:
					t(payload.key, (payload.params ?? {}) as Record<string, string>) ||
					t("invoices.errors.pdfGenerationFailed"),
				variant: "destructive",
			});
		} finally {
			setPdfLoading(false);
		}
	};

	const getStatusBadge = (status: string) => {
		const statusConfig = {
			paid: { variant: "success", text: t("invoices.status.paid") },
			pending: { variant: "warning", text: t("invoices.status.pending") },
			partial: { variant: "default", text: t("invoices.status.partial") },
			overdue: { variant: "destructive", text: t("invoices.status.overdue") },
			cancelled: { variant: "destructive", text: t("invoices.status.cancelled") },
		};

		const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
		return <Badge variant={config.variant as any}>{config.text}</Badge>;
	};

	if (loading) return <Loading />;

	if (!salesTransaction) {
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">{t("invoices.errors.notFound")}</p>
			</div>
		);
	}

	const splitChildren = salesTransaction.splitChildren ?? [];
	const isSplitParent = salesTransaction.splitStatus === "split" && splitChildren.length > 0;
	const displayPaymentStatus = isSplitParent
		? splitChildren.every(share => share.paymentStatus === "paid")
			? "paid"
			: splitChildren.some(share => Number(share.paidAmount) > 0)
				? "partial"
				: "pending"
		: salesTransaction.paymentStatus;
	let invoiceStatus: "paid" | "pending" | "partial" | "overdue" | "cancelled" = "pending";
	if (salesTransaction.status === "cancelled") {
		invoiceStatus = "cancelled";
	} else if (displayPaymentStatus === "paid") {
		invoiceStatus = "paid";
	} else if (displayPaymentStatus === "partial") {
		invoiceStatus = "partial";
	} else if (new Date(salesTransaction.dueDate) < new Date()) {
		invoiceStatus = "overdue";
	} else {
		invoiceStatus = "pending";
	}

	return (
		<div className="container mx-auto max-w-full space-y-4 px-3 py-4 md:px-4 md:py-5">
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("invoices.detail.title")}</PageHeading>
						<p className="text-muted-foreground">
							{t("invoices.detail.description")} #{salesTransaction.invoiceNumber}
						</p>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{!isSplitParent ? (
						<Button variant="outline" onClick={generatePDF} disabled={pdfLoading} aria-busy={pdfLoading}>
							<Download className="h-4 w-4 mx-2" />
							{pdfLoading ? t("common.loading") : t("invoices.actions.downloadPDF")}
						</Button>
					) : null}
					{!isSplitParent ? (
						<PermissionGate permission="sales.update">
							<Button variant="outline" onClick={() => navigate(`/invoices/${id}/edit`)}>
								<Edit className="h-4 w-4 mx-2" />
								{t("common.edit")}
							</Button>
						</PermissionGate>
					) : null}
					{!isSplitParent ? (
						<PermissionGate permission="sales.delete">
							<Button variant="destructive" onClick={handleDelete}>
								<Trash2 className="h-4 w-4 mx-2" />
								{t("common.delete")}
							</Button>
						</PermissionGate>
					) : null}
				</div>
			</div>

			<div className="space-y-4">
				<Card>
					<CardHeader>
						<CardTitle className="flex flex-wrap items-center justify-between gap-3">
							{t("invoices.detail.invoiceInfo")}
							<div className="flex items-center gap-2">
								<Label>{t("invoices.detail.status")}</Label>
								<div>{getStatusBadge(invoiceStatus)}</div>
							</div>
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div className="space-y-2">
								<Label>{t("invoices.detail.invoiceNumber")}</Label>
								<Input
									aria-label={t("invoices.detail.invoiceNumber")}
									value={salesTransaction.invoiceNumber}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("invoices.detail.invoiceDate")}</Label>
								<Input
									aria-label={t("invoices.detail.invoiceDate")}
									value={formatDate(salesTransaction.saleDate)}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("invoices.detail.dueDate")}</Label>
								<Input
									aria-label={t("invoices.detail.dueDate")}
									value={formatDate(salesTransaction.dueDate)}
									readOnly
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label>{t("invoices.detail.notes")}</Label>
							<Input
								aria-label={t("invoices.detail.notes")}
								value={salesTransaction.notes || ""}
								readOnly
							/>
						</div>
					</CardContent>
				</Card>

				{isSplitParent ? (
					<SplitBillSummaryCard
						shares={splitChildren}
						currency={currency}
						onOpenShare={shareId => navigate(`/invoices/${shareId}`)}
					/>
				) : null}
				<Card>
					<CardHeader>
						<CardTitle>{t("invoices.detail.customerInfo")}</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
							<div className="space-y-2">
								<Label>{t("invoices.detail.customerName")}</Label>
								<Input
									aria-label={t("invoices.detail.customerName")}
									value={`${salesTransaction.customer?.firstName || ""} ${salesTransaction.customer?.lastName || ""}`.trim()}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("invoices.detail.customerEmail")}</Label>
								<Input
									aria-label={t("invoices.detail.customerEmail")}
									value={salesTransaction.customer?.email || ""}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("invoices.detail.customerPhone")}</Label>
								<Input
									aria-label={t("invoices.detail.customerPhone")}
									value={salesTransaction.customer?.phone || ""}
									readOnly
									className="rtl:text-right"
									dir="ltr"
								/>
							</div>
							<div className="space-y-2 col-span-3">
								<Label>{t("invoices.detail.customerAddress")}</Label>
								<Input
									aria-label={t("invoices.detail.customerAddress")}
									value={salesTransaction.customer?.address || ""}
									readOnly
								/>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("invoices.detail.items")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{salesTransaction.items.map(item => (
								<div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
									<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
										{item.productImage ? (
											<img
												src={item.productImage}
												alt={item.productName}
												className="w-full h-full object-cover rounded-lg"
											/>
										) : (
											<Package className="h-8 w-8 text-primary" />
										)}
									</div>
									<div className="flex-1">
										<h4 className="font-medium">{item.productName}</h4>
										<div className="grid grid-cols-3 gap-4 mt-2">
											<div>
												<Label className="text-sm">{t("invoices.detail.quantity")}</Label>
												<div className="text-lg font-semibold">{item.quantity}</div>
											</div>
											<div>
												<Label className="text-sm">{t("invoices.detail.unitPrice")}</Label>
												<div className="text-lg font-semibold">
													<Price value={item.unitPrice} currency={currency} />
												</div>
											</div>
											<div>
												<Label className="text-sm">{t("invoices.detail.total")}</Label>
												<div className="text-lg font-semibold">
													<Price value={item.totalAmount} currency={currency} />
												</div>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("invoices.detail.summary")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							<div className="flex justify-between">
								<span>{t("invoices.detail.subtotal")}</span>
								<span className="font-semibold">
									<Price value={salesTransaction.subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("invoices.detail.tax")}</span>
								<span className="font-semibold">
									<Price value={salesTransaction.taxAmount} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("invoices.detail.total")}</span>
									<span>
										<Price value={salesTransaction.totalAmount} currency={currency} />
									</span>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
};

export default InvoiceDetailPage;
